import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import TelegramIcon from "@mui/icons-material/Telegram"
import {
    DialogTitle,
    DialogContent,
    Stack,
    DialogActions,
    DialogContentText,
    Box,
    useTheme,
    Collapse,
    AlertTitle,
} from "@mui/material"
import { Dayjs } from "dayjs"
import { Dispatch, ReactElement, SetStateAction } from "react"
import { useForm } from "react-hook-form"

import { Datasets } from "../../../constants/enums"
import {
    AWAITING_ASSESSMENT,
    COMPLAINT_CONFORMATION,
    COMPLIANT,
    DRAFT_AMENDMENT,
    DRAFT_DEVICE,
    NON_COMPLIANT,
    NON_COMPLIANT_MINOR,
    NON_COMPLIANT_NEW,
} from "../../../constants/generalConstants"
import { useFilterReferenceMappings } from "../../../hooks/useFilterReferenceMappings"
import useAssessDevice from "../../../services/middleware/hooks/deviceRegisters/useAssessDevice"
import { GRID_SPACING } from "../../../themes/defaultThemeParams"
import AppButton from "../../buttons/appButton"
import AppLoadingButton from "../../buttons/appLoadingButton"
import AppAlert from "../../dataDisplay/alerts/appAlert"
import AppDialog from "../../dialog/appDialog"
import ValidatedAutocomplete from "../../inputs/validated/validatedSelect"
import { AppCardHeader } from "../../surfaces/appCard"

export const LCT_STATUS_MAPPINGS = {
    [COMPLIANT]: COMPLIANT,
    [COMPLAINT_CONFORMATION]: COMPLAINT_CONFORMATION,
    [NON_COMPLIANT_MINOR]: NON_COMPLIANT_MINOR,
    [AWAITING_ASSESSMENT]: AWAITING_ASSESSMENT,
    [NON_COMPLIANT]: NON_COMPLIANT,
    [DRAFT_AMENDMENT]: DRAFT_AMENDMENT,
    [DRAFT_DEVICE]: DRAFT_DEVICE,
}

// Only these statuses can be picked by the public
export const LCT_PUBLIC_STATUS = [COMPLIANT, NON_COMPLIANT]

// Statuses the assessor cannot pick
const NON_ASSESSOR_STATUSES = [
    DRAFT_AMENDMENT,
    DRAFT_DEVICE,
    AWAITING_ASSESSMENT,
]

export const LCT_ASSESSOR_STATUS_MAPPINGS = Object.fromEntries(
    Object.entries(LCT_STATUS_MAPPINGS).filter(
        ([key]) => !NON_ASSESSOR_STATUSES.includes(key)
    )
)

const TTR_PROHIBITED_COMPLIANCE_STATUSES = [
    NON_COMPLIANT_NEW,
    DRAFT_AMENDMENT,
    DRAFT_DEVICE,
]

interface IAssessDeviceForm {
    deviceRef: string
    deviceStatus: string
    basedOnPublishedDate: Dayjs
    isOpen: boolean
    setIsOpen: Dispatch<SetStateAction<boolean>>
    mutate: () => void
    mode: "TTR" | "LCT"
    isV2gDevice?: boolean
}

const ASSESSED_COMPLIANCE_STATUS = "assessedComplianceStatus"

/**
 * Dialog where admins can submit assessments on TTR devices.
 * This is an uncontrolled form, registered with react-hook-form.
 * If a component is mounted, it is automatically registered with react-hook-form.
 */
const AssessDeviceForm = ({
    deviceRef,
    deviceStatus,
    basedOnPublishedDate,
    isOpen,
    setIsOpen,
    mutate,
    mode,
    isV2gDevice = false,
}: IAssessDeviceForm): ReactElement => {
    const theme = useTheme()
    const { control, handleSubmit, watch } = useForm({
        shouldUnregister: true,
    })
    const { error, isSending, postTtrResponse, postLctResponse } =
        useAssessDevice(() => setIsOpen(false), isV2gDevice)

    const {
        complianceStatusIds,
        complianceMappings,
        isLoading: dropDownsLoading,
    } = useFilterReferenceMappings()

    const submitResponse = (): void => {
        const ComplianceStatus = watch(ASSESSED_COMPLIANCE_STATUS)
        if (mode === "LCT") {
            postLctResponse(deviceRef, basedOnPublishedDate, Datasets.EVAC, {
                ComplianceStatus,
            }).then(mutate)
        } else {
            postTtrResponse(
                deviceRef,
                basedOnPublishedDate,
                ComplianceStatus
            ).then(mutate)
        }
    }

    const ttrComplianceStatuses = complianceStatusIds.filter(
        (status) =>
            !TTR_PROHIBITED_COMPLIANCE_STATUSES.includes(
                complianceMappings[status]
            ) && complianceMappings[status] !== deviceStatus
    )

    const statusOptions =
        mode === "LCT"
            ? Object.keys(LCT_ASSESSOR_STATUS_MAPPINGS).filter(
                  (status) => status !== deviceStatus
              )
            : ttrComplianceStatuses

    const getOptionLabel = (option: any): string => {
        if (mode === "LCT") {
            return LCT_STATUS_MAPPINGS[
                option as keyof typeof LCT_STATUS_MAPPINGS
            ]
        }
        return complianceMappings[option]
    }

    return (
        <AppDialog isOpen={isOpen}>
            <DialogTitle>
                <AppCardHeader
                    sx={{ p: 0 }}
                    titleTypographyProps={{
                        variant: "h5",
                        fontWeight: "500",
                    }}
                    title={
                        <>
                            Submit assessment for{" "}
                            <Box
                                color={theme.palette.secondary.main}
                                display="inline"
                            >
                                {deviceRef}
                            </Box>{" "}
                        </>
                    }
                    subheader="Select a compliance status for this device and submit to complete your assessment. If you are not marking the device as compliant, please add a comment to the device explaining your assessment to the manufacturer"
                />
            </DialogTitle>
            <DialogContent>
                <Stack spacing={GRID_SPACING} paddingTop="8px">
                    <DialogContentText>
                        Select the compliance status for this device and submit
                        to complete your assessment.
                    </DialogContentText>
                    <Collapse in={!!error} mountOnEnter>
                        <AppAlert severity={error?.level}>
                            <AlertTitle>{error?.message}</AlertTitle>
                            {error?.displayDetail}
                        </AppAlert>
                    </Collapse>
                    <ValidatedAutocomplete
                        control={control}
                        required
                        registerName={ASSESSED_COMPLIANCE_STATUS}
                        label="Select compliance status"
                        options={statusOptions}
                        getOptionLabel={getOptionLabel}
                        loading={dropDownsLoading}
                        disablePortal={false}
                    />
                </Stack>
            </DialogContent>
            <DialogActions>
                <AppButton
                    startIcon={<ArrowBackIcon />}
                    variant="outlined"
                    onClick={() => setIsOpen(false)}
                    sx={{ mr: 1 }}
                >
                    Back
                </AppButton>
                <AppLoadingButton
                    color="success"
                    variant="contained"
                    endIcon={<TelegramIcon />}
                    loading={isSending}
                    onClick={handleSubmit(submitResponse)}
                >
                    Submit
                </AppLoadingButton>
            </DialogActions>
        </AppDialog>
    )
}

export default AssessDeviceForm
